import { useState, useMemo, useEffect, useCallback, ReactNode } from 'react';
import { checkinService, Checkin, getCheckinImageSrc } from '../../api/checkin';
import { vehicleService, Vehicle } from '../../api/vehicles';
import { employeeService, Employee } from '../../api/Employee';
import { getApiErrorMessage } from '../../api';
import {
  ClipboardCheck,
  Search,
  Filter,
  Plus,
  Eye,
  CheckCircle,
  Timer,
  CheckCircle2,
  Car,
  Users,
  CircleDot,
  Clock,
  PlayCircle,
  Loader2,
  AlertCircle,
  XCircle,
  Upload,
  X,
  Image as ImageIcon,
  FileText,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  StatCard,
  Button,
  Badge,
  Input,
  Select,
  Modal,
  Label,
  Textarea,
} from '../../components/ui/SharedLayout';

interface CheckinFormState {
  vehicleId: string;
  employeeId: string;
  notes: string;
  checkinImage: string | null;
}

const MAX_NOTAS = 400;

const formatDuration = (seconds: number) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  const hh = hrs.toString().padStart(2, '0');
  const mm = mins.toString().padStart(2, '0');
  const ss = secs.toString().padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
};

type FieldWrapperProps = {
  children: ReactNode;
  error?: string;
  label?: ReactNode;
  htmlFor?: string;
  required?: boolean;
  hint?: string;
};

const FieldWrapper = ({ children, error, label, htmlFor, required, hint }: FieldWrapperProps) => (
  <div className="flex flex-col gap-1">
    {label ? (
      <Label htmlFor={htmlFor} className="mb-0.5">
        <span className="inline-flex items-center gap-0.5">
          {label}
          {required ? <span className="text-rose-500 ml-0.5 font-bold leading-none">*</span> : null}
        </span>
      </Label>
    ) : null}
    {children}
    {hint && !error ? (
      <p className="mt-0.5 text-[11px] font-medium leading-tight text-slate-500">{hint}</p>
    ) : null}
    {error ? (
      <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold leading-tight text-rose-600">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        {error}
      </p>
    ) : null}
  </div>
);

const CharCounter = ({ value, max }: { value: string; max: number }) => (
  <span className={`shrink-0 text-[11px] font-semibold tabular-nums ${value.length > max ? 'text-rose-600' : 'text-slate-500'}`}>
    {value.length}/{max}
  </span>
);

const CheckIn = () => {
const [checkIns, setCheckIns] = useState<Checkin[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkInSelecionado, setCheckInSelecionado] = useState<Checkin | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('todos');
  const [filterMotorista, setFilterMotorista] = useState<string>('todos');
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalSrc, setImageModalSrc] = useState<string | null>(null);
  const ITEMS_PER_PAGE = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [timers, setTimers] = useState<{ [key: number]: number }>({});
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [finalizeTarget, setFinalizeTarget] = useState<Checkin | null>(null);
  const [submittingAction, setSubmittingAction] = useState(false);
  const [finalizeImage, setFinalizeImage] = useState<string | null>(null);
  const [formData, setFormData] = useState<CheckinFormState>({
    vehicleId: '',
    employeeId: '',
    notes: '',
    checkinImage: null,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formFinalizeErrors, setFormFinalizeErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const activeFiltersCount = useMemo(() => {
    let n = 0;
    if (filterStatus !== 'todos') n++;
    if (filterMotorista !== 'todos') n++;
    return n;
  }, [filterStatus, filterMotorista]);

  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 2800); return () => clearTimeout(t); } }, [toast]);

  const readFileAsDataUrl = async (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Falha ao ler o arquivo.'));
      reader.readAsDataURL(file);
    });

  const toRawBase64 = (value?: string | null) =>
    value ? value.replace(/^data:image\/\w+;base64,/, '') : undefined;

  const filteredCheckIns = useMemo(() => {
    let filtered = [...checkIns];
    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(checkIn => {
        const driver = employees.find(e => e.id === checkIn.employee_id);
        const vehicle = vehicles.find(v => v.id === checkIn.vehicle_id);
        const haystack = [
          checkIn.id.toString(),
          checkIn.trip_id?.toString() || '',
          checkIn.vehicle_id.toString(),
          checkIn.employee_id.toString(),
          checkIn.status.toLowerCase(),
          driver?.name?.toLowerCase() || '',
          driver?.cpf?.toLowerCase() || '',
          vehicle?.brand?.toLowerCase() || '',
          vehicle?.license_plate?.toLowerCase() || '',
          vehicle?.description?.toLowerCase() || '',
          (checkIn.notes || '').toLowerCase(),
        ].join(' ');
        return haystack.includes(term);
      });
    }
    if (filterStatus !== 'todos') {
      filtered = filtered.filter(c => c.status === filterStatus);
    }
    if (filterMotorista !== 'todos') {
      filtered = filtered.filter(c => String(c.employee_id) === filterMotorista);
    }
    return filtered;
  }, [searchTerm, checkIns, filterStatus, filterMotorista, employees, vehicles]);

  const totalPages = Math.max(1, Math.ceil(filteredCheckIns.length / ITEMS_PER_PAGE));

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const pageRange = useMemo(() => {
    const start = filteredCheckIns.length === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const end = Math.min(currentPage * ITEMS_PER_PAGE, filteredCheckIns.length);
    return { start, end, total: filteredCheckIns.length, totalPages };
  }, [filteredCheckIns, currentPage, ITEMS_PER_PAGE, totalPages]);

  const pagedCheckIns = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredCheckIns.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredCheckIns, currentPage, ITEMS_PER_PAGE]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const sortCheckInsByDate = (items: Checkin[]) =>
    [...items].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  useEffect(() => {
    const interval = setInterval(() => {
      setTimers((prev) => {
        const updated: { [key: number]: number } = { ...prev };
        checkIns.forEach((checkIn) => {
          if (checkIn.status === 'in_progress') {
            const start = new Date(checkIn.date).getTime();
            updated[checkIn.id] = Math.floor((Date.now() - start) / 1000);
          }
        });
        return updated;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [checkIns]);

  const fetchCheckIns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const checkIns = await checkinService.getAll();
      setCheckIns(sortCheckInsByDate(checkIns));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao buscar check-ins.'));
    } finally {
      setLoading(false);
    }
  }, []);
  const fetchVehicles = useCallback(async () => {
    try {
      const vehicles = await vehicleService.getAllVehicles();
      setVehicles(vehicles);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao buscar veículos.'));
      setVehicles([]);
    }
  }, []);
  const fetchEmployees = useCallback(async () => {
    try {
      const employees = await employeeService.getAllEmployees();
      setEmployees(employees);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao buscar funcionários.'));
      setEmployees([]);
    }
  }, []);

  useEffect(() => {
    void fetchCheckIns();
    void fetchVehicles();
    void fetchEmployees();
  }, [fetchCheckIns, fetchVehicles, fetchEmployees]);

  const resetCreateForm = () => {
    setFormData({ vehicleId: '', employeeId: '', notes: '', checkinImage: null });
    setFormErrors({});
  };

  const openCreateModal = () => {
    resetCreateForm();
    setIsCreateModalOpen(true);
  };

  const limparFiltros = () => {
    setFilterStatus('todos');
    setFilterMotorista('todos');
    setSearchTerm('');
  };

  const handleCreateCheckIn = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!formData.vehicleId) nextErrors.vehicleId = 'Selecione um veículo';
    if (!formData.employeeId) nextErrors.employeeId = 'Selecione um motorista';
    if (formData.notes && String(formData.notes).length > MAX_NOTAS) nextErrors.notes = `Muito longo (máx. ${MAX_NOTAS})`;
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }
    setSubmittingAction(true);
    try {
      await checkinService.create({
        vehicle_id: Number(formData.vehicleId),
        employee_id: Number(formData.employeeId),
        notes: formData.notes.trim() || undefined,
        checkinImage: toRawBase64(formData.checkinImage),
        date: new Date().toISOString(),
      });
      await fetchCheckIns();
      setIsCreateModalOpen(false);
      resetCreateForm();
      setToast({ type: 'success', message: 'Check-in iniciado com sucesso!' });
    } catch (err) {
      setFormErrors({ _geral: getApiErrorMessage(err, 'Erro ao iniciar check-in.') });
    } finally {
      setSubmittingAction(false);
    }
  };

  const handleFinalizeCheckIn = async () => {
    if (!finalizeTarget) return;
    const nextErrors: Record<string, string> = {};
    if (!finalizeImage) nextErrors.checkoutImage = 'Anexe a foto de check-out para finalizar';
    if (Object.keys(nextErrors).length > 0) {
      setFormFinalizeErrors(nextErrors);
      return;
    }
    setSubmittingAction(true);
    setFormFinalizeErrors({});
    try {
      await checkinService.create({
        checkinId: finalizeTarget.id,
        checkoutImage: toRawBase64(finalizeImage),
      });
      await fetchCheckIns();
      setFinalizeTarget(null);
      setFinalizeImage(null);
      setToast({ type: 'success', message: 'Check-in finalizado com sucesso!' });
    } catch (err) {
      setFormFinalizeErrors({ _geral: getApiErrorMessage(err, 'Erro ao finalizar check-in.') });
    } finally {
      setSubmittingAction(false);
    }
  };

  const formatDate = (d?: string) => (!d ? '-' : new Date(d).toLocaleDateString('pt-BR'));
  const formatTime = (d?: string) => (!d ? '' : new Date(d).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }));

  return (
    <>
      <PageHeader title="Check-In" hideTitleInBody />

      {toast && (
        <div className={`fixed top-5 right-5 z-[100] inline-flex items-center gap-2.5 rounded-xl px-4 py-3 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.35)] border animate-[fadeIn_0.2s_ease-out] ${toast.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
          {toast.type === 'success' ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> : <XCircle className="h-5 w-5 text-rose-600 shrink-0" />}
          <span className="text-[13px] font-semibold leading-tight">{toast.message}</span>
        </div>
      )}

      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[15px] font-semibold leading-tight text-emerald-600">Registros de entrada e saída</p>
            <h1 className="mt-1 text-[22px] sm:text-[26px] font-bold leading-[1.15] tracking-tight text-slate-900">
              Acompanhe os check-ins de veículos e motoristas.
              <span className="ml-1.5 text-slate-500 font-semibold text-[18px] sm:text-[20px]">
                Visualize durações em tempo real.
              </span>
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="primary" size="lg" onClick={openCreateModal} disabled={loading || submittingAction}>
              <Plus className="h-[18px] w-[18px] stroke-[2.1]" />
              Iniciar check-in
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4 mb-7">
          <StatCard
            icon={<ClipboardCheck className="h-5 w-5 stroke-[1.8]" />}
            label="Total de Check-ins"
            value={checkIns.length}
            trend={<span>Registros totais</span>}
            variant="deep"
          />
          <StatCard
            icon={<PlayCircle className="h-5 w-5 stroke-[1.8]" />}
            label="Em Andamento"
            value={checkIns.filter(c => c.status === 'in_progress').length}
            trend={<span>Ativos no momento</span>}
            variant="primary"
          />
          <StatCard
            icon={<CheckCircle2 className="h-5 w-5 stroke-[1.8]" />}
            label="Concluídos"
            value={checkIns.filter(c => c.status === 'finished').length}
            trend={<span>Finalizados com sucesso</span>}
            variant="sky"
          />
          <StatCard
            icon={<Timer className="h-5 w-5 stroke-[1.8]" />}
            label="Últimas 24h"
            value={checkIns.filter(c => Date.now() - new Date(c.date).getTime() < 86400000).length}
            trend={<span>Movimento recente</span>}
            variant="rich"
          />
        </div>

        {error && (
          <div className="mb-5 inline-flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 w-full">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
            <div className="flex flex-col gap-0.5">
              <span className="text-[13px] font-bold leading-tight">Não foi possível carregar</span>
              <span className="text-[12.5px] font-medium leading-tight text-rose-700/90">{error}</span>
            </div>
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div className="flex w-full flex-col gap-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                    Check-ins
                  </h2>
                  <p className="text-[13px] text-slate-500 mt-0.5">
                    {loading
                      ? 'Carregando registros...'
                      : pageRange.total === 0
                        ? 'Nenhum check-in encontrado'
                        : `${pageRange.start} a ${pageRange.end} de ${pageRange.total} registros listados (total cadastrado: ${checkIns.length})`}
                  </p>
                </div>
                <div className="w-full sm:max-w-sm relative">
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 stroke-[1.8] text-slate-400" aria-hidden="true" />
                  <Input
                    type="text"
                    name="search-checkins"
                    placeholder="Buscar motorista, veículo, placa..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={loading}
                    className="pl-10 h-10"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
                <div className="sm:col-span-4">
                  <div className="relative">
                    <Filter className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 stroke-[1.9] text-slate-400" />
                    <Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="pl-10 h-10"
                    >
                      <option value="todos">Todos os status</option>
                      <option value="in_progress">Em andamento</option>
                      <option value="finished">Concluído</option>
                    </Select>
                  </div>
                </div>
                <div className="sm:col-span-5">
                  <Select
                    value={filterMotorista}
                    onChange={(e) => setFilterMotorista(e.target.value)}
                    className="h-10"
                  >
                    <option value="todos">Todos os motoristas</option>
                    {employees.map(e => (
                      <option key={e.id} value={String(e.id)}>{e.name}{e.cargo ? ` — ${e.cargo}` : ''}</option>
                    ))}
                  </Select>
                </div>
                <div className="sm:col-span-3 sm:flex sm:items-center sm:justify-end">
                  {activeFiltersCount > 0 ? (
                    <Button variant="secondary" onClick={limparFiltros} className="sm:justify-center w-full sm:w-auto">
                      <Filter className="h-4 w-4 stroke-[1.8]" />
                      <span className="inline-flex items-center gap-1.5">
                        Limpar filtros
                        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-600 px-1.5 text-[10.5px] font-bold text-white shadow-sm">
                          {activeFiltersCount}
                        </span>
                      </span>
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          </CardHeader>

          <CardBody className="!py-3">
            {loading && checkIns.length === 0 ? (
              <div className="flex justify-center items-center py-16 gap-2.5">
                <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
                <span className="text-[13.5px] font-semibold text-slate-600">
                  Carregando check-ins...
                </span>
              </div>
            ) : (
              <>
              <ModernTable className="table-fixed layout-fixed">
                <Thead>
                  <Tr>
                    <Th className="w-[22%] min-w-[220px] max-w-[320px]">Motorista</Th>
                    <Th className="w-[20%] min-w-[190px] max-w-[280px]">Veículo</Th>
                    <Th className="w-[12%] min-w-[140px] max-w-[170px]">Início</Th>
                    <Th className="w-[10%] min-w-[110px] max-w-[140px]">Duração</Th>
                    <Th className="w-[10%] min-w-[105px] max-w-[135px]">Status</Th>
                    <Th className="w-[9%] min-w-[115px] max-w-[150px]">Prova</Th>
                    <Th className="w-[12%] min-w-[140px] max-w-[170px]">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {pageRange.total === 0 ? (
                    <Tr>
                      <Td colSpan={7}>
                        <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                            <ClipboardCheck className="h-7 w-7 stroke-[1.8]" />
                          </div>
                          <div className="text-[14px] font-bold text-slate-700">Nenhum check-in encontrado</div>
                          <p className="max-w-sm text-[12.5px] font-medium leading-relaxed text-slate-500">
                            {searchTerm || activeFiltersCount > 0 ? 'Nenhum resultado para busca/filtros atuais. Tente ajustar os critérios.' : 'Clique em "Iniciar check-in" para criar o primeiro registro.'}
                          </p>
                        </div>
                      </Td>
                    </Tr>
                  ) : (
                    pagedCheckIns.map((checkIn) => {
                      const driver = employees.find(e => e.id === checkIn.employee_id);
                      const driverName = driver?.name || String(checkIn.employee_id);
                      const vehicle = vehicles.find(v => v.id === checkIn.vehicle_id);
                      const vehicleBrand = vehicle?.brand || String(checkIn.vehicle_id);
                      const vehicleLicense = vehicle?.license_plate || '';
                      return (
                        <Tr key={checkIn.id}>
                          <Td>
                            <div className="min-w-0 w-full max-w-[300px] overflow-hidden">
                              <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700 text-white shadow-[0_6px_16px_-8px_rgba(16,185,129,0.6)]">
                                  <Users className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="min-w-0 w-full">
                                    <span className="block truncate font-semibold text-slate-900" title={driverName}>
                                      {driverName}
                                    </span>
                                  </div>
                                  <div className="mt-0.5 text-[12px] text-slate-500">
                                    ID #{checkIn.employee_id}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[260px] overflow-hidden">
                              <div className="flex items-center gap-2.5">
                                <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
                                  <Car className="h-[16px] w-[16px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="min-w-0 w-full">
                                    <span className="block truncate font-semibold text-slate-800" title={vehicleBrand}>
                                      {vehicleBrand}
                                    </span>
                                  </div>
                                  {vehicleLicense && (
                                    <div className="mt-0.5 inline-flex items-center">
                                      <span className="font-mono text-[11.5px] tracking-wide text-slate-500 rounded-md bg-slate-100 px-1.5 py-0.5">
                                        {vehicleLicense}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1.5 text-slate-700">
                              <Clock className="h-3.5 w-3.5 stroke-[1.8] text-slate-400 shrink-0" />
                              <div className="flex flex-col">
                                <span className="font-medium tabular-nums text-[13px]">
                                  {formatDate(checkIn.date)}
                                </span>
                                {checkIn.date && (
                                  <span className="text-[12px] text-slate-500 tabular-nums">
                                    {formatTime(checkIn.date)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="inline-flex items-center gap-1.5 text-slate-700">
                              <CircleDot className="h-3.5 w-3.5 stroke-[1.8] text-emerald-500 shrink-0" />
                              <span className="font-mono font-semibold tabular-nums text-[13.5px]">
                                {formatDuration(timers[checkIn.id] || 0)}
                              </span>
                            </div>
                          </Td>
                          <Td>
                            {checkIn.status === 'in_progress' ? (
                              <Badge tone="blue">
                                <span className="inline-flex items-center gap-1.5">
                                  <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
                                  Em andamento
                                </span>
                              </Badge>
                            ) : (
                              <Badge tone="emerald">
                                <span className="inline-flex items-center gap-1.5">
                                  <CheckCircle className="h-3 w-3 stroke-[2.2]" />
                                  Concluído
                                </span>
                              </Badge>
                            )}
                          </Td>
                          <Td>
                            {checkIn.images && checkIn.images.length > 0 ? (
                              <div className="min-w-0 w-full max-w-[150px] overflow-hidden flex items-center gap-1.5">
                                {checkIn.images.slice(0, 2).map((img, idx) => (
                                  <img
                                    key={idx}
                                    src={getCheckinImageSrc(img)}
                                    alt={`Prova ${idx + 1}`}
                                    onClick={() => {
                                      setCheckInSelecionado(checkIn);
                                      setImageModalSrc(getCheckinImageSrc(img));
                                      setImageModalOpen(true);
                                    }}
                                    className="h-9 w-9 shrink-0 cursor-zoom-in rounded-lg border border-slate-200 object-cover hover:opacity-90 hover:ring-2 hover:ring-emerald-300 transition-all"
                                  />
                                ))}
                                {checkIn.images.length > 2 ? (
                                  <span className="shrink-0 inline-flex h-9 min-w-[28px] items-center justify-center rounded-lg bg-slate-100 px-1.5 text-[10.5px] font-bold text-slate-600 ring-1 ring-slate-200">
                                    +{checkIn.images.length - 2}
                                  </span>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-[12px] font-medium text-slate-400">—</span>
                            )}
                          </Td>
                          <Td className="!text-right">
                            <div className="inline-flex items-center gap-1 justify-start flex-wrap">
                              <Button variant="ghost" size="icon" onClick={() => { setCheckInSelecionado(checkIn); }} title="Ver detalhes" className="h-8 w-8 rounded-lg text-slate-500 hover:text-blue-700 hover:bg-blue-50 hover:ring-blue-200">
                                <Eye className="h-4 w-4 stroke-[1.9]" />
                              </Button>
                              {checkIn.status === 'in_progress' && (
                                <Button variant="success" size="sm" onClick={() => { setFinalizeTarget(checkIn); setFinalizeImage(null); setFormFinalizeErrors({}); }} title="Finalizar check-in">
                                  <CheckCircle className="h-4 w-4 stroke-[2]" />
                                  Finalizar
                                </Button>
                              )}
                            </div>
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </ModernTable>

              {!loading && (
                <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-[13px] font-medium text-slate-500">
                    Mostrando <span className="font-semibold text-slate-700">{pageRange.start}-{pageRange.end}</span> de <span className="font-semibold text-slate-700">{pageRange.total}</span> resultados
                  </div>
                  <div className="inline-flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      aria-label="Primeira página"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                    >
                      <ChevronsLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      aria-label="Página anterior"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                    >
                      <ChevronLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                    </button>
                    <div className="inline-flex items-center gap-1 mx-1">
                      {getPageNumbers.map((n, idx) =>
                        n === '...' ? (
                          <span
                            key={`dots-${idx}`}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setCurrentPage(n)}
                            aria-label={`Ir para página ${n}`}
                            className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 ${
                              n === currentPage
                                ? 'bg-emerald-600 text-white shadow-[0_4px_14px_-4px_rgba(16,185,129,0.55)]'
                                : 'border border-slate-200 bg-white text-slate-700 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700'
                            }`}
                          >
                            {n}
                          </button>
                        ),
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      aria-label="Próxima página"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                    >
                      <ChevronRight className="h-[16px] w-[16px] stroke-[1.8]" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      aria-label="Última página"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                    >
                      <ChevronsRight className="h-[16px] w-[16px] stroke-[1.8]" />
                    </button>
                  </div>
                </div>
              )}
              </>
            )}
          </CardBody>
        </SectionCard>

        {checkInSelecionado && (
          <Modal
            onClose={() => setCheckInSelecionado(null)}
            maxWidthClass="max-w-4xl"
            title={
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 text-white shadow-[0_6px_16px_-8px_rgba(16,185,129,0.6)]">
                  <ClipboardCheck className="h-5 w-5 stroke-[1.9]" />
                </div>
                <div>
                  <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                    Detalhes do Check-In #{checkInSelecionado.id}
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 text-[12px] font-medium text-slate-500">
                    <Clock className="h-3.5 w-3.5 stroke-[1.8]" />
                    <span className="tabular-nums">{new Date(checkInSelecionado.date).toLocaleString('pt-BR')}</span>
                    <span className="text-slate-300">·</span>
                    {checkInSelecionado.status === 'in_progress' ? (
                      <Badge tone="blue">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
                          Em andamento
                        </span>
                      </Badge>
                    ) : (
                      <Badge tone="emerald">Concluído</Badge>
                    )}
                  </div>
                </div>
              </div>
            }
            footer={
              <>
                <Button variant="secondary" onClick={() => setCheckInSelecionado(null)} className="h-11 px-5">
                  Fechar
                </Button>
                {checkInSelecionado.status === 'in_progress' && (
                  <Button
                    variant="success"
                    className="h-11 px-6 shadow-[0_6px_16px_-8px_rgba(16,185,129,0.7)]"
                    onClick={() => {
                      setFinalizeTarget(checkInSelecionado);
                      setFinalizeImage(null);
                      setFormFinalizeErrors({});
                      setCheckInSelecionado(null);
                    }}
                  >
                    <CheckCircle2 className="h-4 w-4 stroke-[2.1]" />
                    Finalizar este check-in
                  </Button>
                )}
              </>
            }
          >
            <div className="space-y-5">
              <div className="-mx-6 -mt-6 px-6 py-5 rounded-t-2xl bg-slate-50/40 border-b border-slate-200/70">
                <div className="flex items-center gap-2.5">
                  <span className="h-6 w-1 rounded-full bg-gradient-to-b from-emerald-400 to-teal-500" aria-hidden="true" />
                  <h3 className="text-[11.5px] font-bold uppercase tracking-[0.14em] leading-none text-emerald-700">
                    Identificação
                  </h3>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="mb-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                      <Users className="h-3.5 w-3.5 stroke-[1.8] text-emerald-600" />
                      Motorista
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800">
                      {employees.find(e => e.id === checkInSelecionado.employee_id)?.name || String(checkInSelecionado.employee_id)}
                    </div>
                  </div>
                  <div>
                    <div className="mb-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                      <Car className="h-3.5 w-3.5 stroke-[1.8] text-sky-600" />
                      Veículo
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800">
                      {(() => {
                        const v = vehicles.find(vv => vv.id === checkInSelecionado.vehicle_id);
                        if (!v) return String(checkInSelecionado.vehicle_id);
                        const placa = v.license_plate ? ` • ${v.license_plate}` : '';
                        return `${v.brand || String(v.id)}${placa}`;
                      })()}
                    </div>
                  </div>
                  <div>
                    <div className="mb-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                      <Clock className="h-3.5 w-3.5 stroke-[1.8] text-indigo-600" />
                      Início
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold tabular-nums text-slate-800">
                      {new Date(checkInSelecionado.date).toLocaleString('pt-BR')}
                    </div>
                  </div>
                  <div>
                    <div className="mb-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                      <Timer className="h-3.5 w-3.5 stroke-[1.8] text-emerald-600" />
                      Duração
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold tabular-nums text-slate-800">
                      <div className="inline-flex items-center gap-2">
                        <CircleDot className="h-3.5 w-3.5 stroke-[1.8] text-emerald-500 shrink-0" />
                        <span className="font-mono text-[14px]">
                          {formatDuration(timers[checkInSelecionado.id] || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2.5">
                  <span className="h-6 w-1 rounded-full bg-gradient-to-b from-sky-400 to-indigo-500" aria-hidden="true" />
                  <h3 className="text-[11.5px] font-bold uppercase tracking-[0.14em] leading-none text-sky-700">
                    Observações e evidências
                  </h3>
                </div>

                <div>
                  <div className="mb-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    <FileText className="h-3.5 w-3.5 stroke-[1.8] text-indigo-600" />
                    Observações
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-800 min-h-[72px] whitespace-pre-wrap leading-relaxed">
                    {checkInSelecionado.notes || <span className="text-slate-400 italic">Nenhuma observação registrada.</span>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                      <ImageIcon className="h-3.5 w-3.5 stroke-[1.8] text-emerald-600" />
                      Foto de Check-in
                    </div>
                    {Array.isArray(checkInSelecionado.images) && checkInSelecionado.images[0] ? (
                      <div className="group relative overflow-hidden rounded-2xl border border-slate-200 ring-1 ring-inset ring-slate-900/[0.04] shadow-sm">
                        <img
                          src={getCheckinImageSrc(checkInSelecionado.images[0])}
                          alt="Prova de check-in"
                          className="w-full h-48 object-cover cursor-zoom-in transition duration-200 group-hover:scale-[1.03]"
                          onClick={() => { setImageModalSrc(getCheckinImageSrc(checkInSelecionado.images[0])); setImageModalOpen(true); }}
                        />
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-9 text-center">
                        <ImageIcon className="h-9 w-9 stroke-[1.6] text-slate-400" />
                        <div className="text-[12.5px] font-medium text-slate-500">Foto não disponível</div>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                      <CheckCircle2 className="h-3.5 w-3.5 stroke-[1.8] text-teal-600" />
                      Foto de Check-out
                    </div>
                    {Array.isArray(checkInSelecionado.images) && checkInSelecionado.images[1] ? (
                      <div className="group relative overflow-hidden rounded-2xl border border-slate-200 ring-1 ring-inset ring-slate-900/[0.04] shadow-sm">
                        <img
                          src={getCheckinImageSrc(checkInSelecionado.images[1])}
                          alt="Prova de check-out"
                          className="w-full h-48 object-cover cursor-zoom-in transition duration-200 group-hover:scale-[1.03]"
                          onClick={() => { setImageModalSrc(getCheckinImageSrc(checkInSelecionado.images[1])); setImageModalOpen(true); }}
                        />
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-9 text-center">
                        <ImageIcon className="h-9 w-9 stroke-[1.6] text-slate-400" />
                        <div className="text-[12.5px] font-medium text-slate-500">Foto não disponível</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Modal>
        )}
      </div>

      {imageModalOpen && imageModalSrc && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 sm:px-6" onClick={() => setImageModalOpen(false)}>
          <div className="absolute inset-0 bg-slate-950/75 backdrop-blur-sm" aria-hidden="true" />
          <div className="relative z-10 overflow-hidden rounded-2xl border border-white/10 bg-slate-900 shadow-[0_30px_80px_-20px_rgba(15,23,42,0.75)]" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => setImageModalOpen(false)} className="absolute top-3 right-3 z-20 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900/80 text-white ring-1 ring-inset ring-white/10 backdrop-blur transition-colors hover:bg-slate-800 hover:text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/50" title="Fechar" aria-label="Fechar visualização">
              <X className="h-5 w-5 stroke-[2]" />
            </button>
            <div className="bg-[radial-gradient(ellipse_at_center,rgba(30,41,59,0.55),transparent_60%)] p-4 sm:p-6">
              <img src={imageModalSrc} alt="Visualização ampliada" className="max-h-[80vh] max-w-[min(90vw,1000px)] rounded-xl object-contain shadow-[0_12px_40px_-12px_rgba(0,0,0,0.65)]" />
            </div>
          </div>
        </div>
      )}

      {isCreateModalOpen && (
        <Modal
          onClose={() => { if (!submittingAction) setIsCreateModalOpen(false); }}
          maxWidthClass="max-w-2xl"
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700 text-white shadow-[0_6px_16px_-8px_rgba(16,185,129,0.6)]">
                <PlayCircle className="h-5 w-5 stroke-[1.9]" />
              </div>
              <div>
                <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">Iniciar novo check-in</div>
                <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                  Selecione o motorista e o veículo para começar
                </div>
              </div>
            </div>
          }
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
              <button type="button" onClick={() => setIsCreateModalOpen(false)} disabled={submittingAction} className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30 disabled:pointer-events-none disabled:opacity-60">
                Cancelar
              </button>
              <button type="submit" form="checkin-form" disabled={submittingAction} className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-emerald-600 via-emerald-700 to-green-700 px-6 text-[13px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(16,185,129,0.55)] transition-all hover:shadow-[0_8px_22px_-6px_rgba(16,185,129,0.65)] focus:outline-none focus:ring-4 focus:ring-emerald-500/25 disabled:pointer-events-none disabled:opacity-60 disabled:shadow-none">
                {submittingAction ? <><Loader2 className="h-4 w-4 animate-spin stroke-[2]" /> Iniciando...</> : <><Plus className="h-4 w-4 stroke-[2.1]" /> Iniciar check-in</>}
              </button>
            </div>
          }
        >
          <form id="checkin-form" onSubmit={handleCreateCheckIn} className="space-y-5" noValidate>
            {formErrors._geral && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-3.5 flex items-start gap-2.5">
                <AlertCircle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                <span className="text-[12.5px] font-semibold leading-tight text-rose-800">{formErrors._geral}</span>
              </div>
            )}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 to-green-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-emerald-700">
                  Motorista e veículo
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <FieldWrapper label={<span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Motorista</span>} htmlFor="checkin-motorista" required error={formErrors.employeeId}>
                    <Select id="checkin-motorista" name="employeeId" value={formData.employeeId} onChange={(e) => {
                      setFormData((c) => ({ ...c, employeeId: e.target.value }));
                      if (formErrors.employeeId) setFormErrors((p) => { const n = { ...p }; delete n.employeeId; return n; });
                    }}>
                      <option value="">Selecione o motorista</option>
                      {employees.map(e => (<option key={e.id} value={String(e.id)}>{e.name}{e.cargo ? ` — ${e.cargo}` : ''}</option>))}
                    </Select>
                  </FieldWrapper>
                  <FieldWrapper label={<span className="inline-flex items-center gap-1"><Car className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Veículo</span>} htmlFor="checkin-veiculo" required error={formErrors.vehicleId}>
                    <Select id="checkin-veiculo" name="vehicleId" value={formData.vehicleId} onChange={(e) => {
                      setFormData((c) => ({ ...c, vehicleId: e.target.value }));
                      if (formErrors.vehicleId) setFormErrors((p) => { const n = { ...p }; delete n.vehicleId; return n; });
                    }}>
                      <option value="">Selecione o veículo</option>
                      {vehicles.map(v => (<option key={v.id} value={String(v.id)}>{v.brand}{v.license_plate ? ` — ${v.license_plate}` : ''}</option>))}
                    </Select>
                  </FieldWrapper>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">
                  Observações e evidência
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-4">
                <FieldWrapper label={<span className="inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Notas / Observações</span>} htmlFor="checkin-notas" error={formErrors.notes}>
                  <div>
                    <div className="mb-1.5 flex items-end justify-between gap-2">
                      <span className="h-0 w-0" aria-hidden />
                      <CharCounter value={formData.notes || ''} max={MAX_NOTAS} />
                    </div>
                    <Textarea id="checkin-notas" name="notes" value={formData.notes} onChange={(e) => {
                      setFormData((c) => ({ ...c, notes: e.target.value }));
                      if (formErrors.notes) setFormErrors((p) => { const n = { ...p }; delete n.notes; return n; });
                    }} rows={3} placeholder="Condições do veículo, hodômetro, observações gerais..." className={`resize-y ${formErrors.notes ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-500/20' : ''}`} maxLength={MAX_NOTAS + 50} />
                  </div>
                </FieldWrapper>

                <FieldWrapper label={<span className="inline-flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Foto de check-in</span>} hint="JPG, PNG até 5MB · opcional">
                  <label className="group flex min-h-[110px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/40 px-4 py-6 text-center transition-all hover:border-emerald-400 hover:bg-emerald-50/40 focus-within:border-emerald-500 focus-within:ring-4 focus-within:ring-emerald-500/10">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-slate-400 ring-1 ring-slate-200 shadow-sm transition-all group-hover:text-emerald-600 group-hover:ring-emerald-200">
                      <Upload className="h-5 w-5 stroke-[1.9]" />
                    </div>
                    <div className="text-[13px] font-semibold text-slate-700 group-hover:text-emerald-700">Clique para anexar foto</div>
                    <div className="text-[12px] font-medium text-slate-500">ou arraste e solte o arquivo</div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (event) => {
                        const file = event.target.files?.[0];
                        if (!file) return;
                        try {
                          const imageData = await readFileAsDataUrl(file);
                          setFormData((c) => ({ ...c, checkinImage: imageData }));
                        } catch (err) {
                          setFormErrors((p) => ({ ...p, checkinImage: getApiErrorMessage(err, 'Erro ao carregar foto.') }));
                        }
                      }}
                      className="sr-only"
                    />
                  </label>
                  {formErrors.checkinImage && (
                    <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold leading-tight text-rose-600">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                      {formErrors.checkinImage}
                    </p>
                  )}
                  {formData.checkinImage && (
                    <div className="mt-3 inline-flex relative group/img overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm w-fit">
                      <img src={formData.checkinImage} alt="Prévia check-in" className="h-24 w-24 object-cover transition-transform group-hover/img:scale-[1.03]" />
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/55 to-transparent opacity-0 transition-opacity group-hover/img:opacity-100" />
                      <button
                        type="button"
                        onClick={() => setFormData((c) => ({ ...c, checkinImage: null }))}
                        className="absolute top-1 right-1 inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-black/75 text-white opacity-0 shadow-lg transition-all hover:bg-rose-600 group-hover/img:opacity-100 focus:outline-none focus:ring-2 focus:ring-rose-400/40 focus:ring-offset-1"
                        title="Remover foto"
                        aria-label="Remover foto check-in"
                      >
                        <X className="h-3.5 w-3.5 stroke-[2.2]" />
                      </button>
                    </div>
                  )}
                </FieldWrapper>
              </div>
            </div>
          </form>
        </Modal>
      )}

      {finalizeTarget && (
        <Modal
          onClose={() => { if (!submittingAction) setFinalizeTarget(null); }}
          maxWidthClass="max-w-xl"
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-green-500 via-emerald-600 to-teal-700 text-white shadow-[0_6px_16px_-8px_rgba(16,185,129,0.6)]">
                <CheckCircle2 className="h-5 w-5 stroke-[1.9]" />
              </div>
              <div>
                <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">Finalizar check-in</div>
                <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                  Anexe a foto de retorno (check-out) para concluir
                </div>
              </div>
            </div>
          }
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
              <button type="button" onClick={() => setFinalizeTarget(null)} disabled={submittingAction} className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30 disabled:pointer-events-none disabled:opacity-60">
                Cancelar
              </button>
              <button type="button" onClick={() => void handleFinalizeCheckIn()} disabled={submittingAction} className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-green-600 via-emerald-700 to-teal-700 px-6 text-[13px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(16,185,129,0.55)] transition-all hover:shadow-[0_8px_22px_-6px_rgba(16,185,129,0.65)] focus:outline-none focus:ring-4 focus:ring-emerald-500/25 disabled:pointer-events-none disabled:opacity-60 disabled:shadow-none">
                {submittingAction ? <><Loader2 className="h-4 w-4 animate-spin stroke-[2]" /> Finalizando...</> : <><CheckCircle className="h-4 w-4 stroke-[2]" /> Finalizar check-in</>}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            {formFinalizeErrors._geral && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-3.5 flex items-start gap-2.5">
                <AlertCircle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                <span className="text-[12.5px] font-semibold leading-tight text-rose-800">{formFinalizeErrors._geral}</span>
              </div>
            )}
            <div className="rounded-xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-teal-50/40 p-4 ring-1 ring-inset ring-emerald-100/70">
              <div className="text-[11.5px] font-bold uppercase tracking-[0.14em] text-emerald-700 mb-2">Resumo do registro</div>
              <div className="space-y-1.5 text-[13px] text-slate-800">
                <div className="flex items-start gap-2">
                  <Users className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold">Motorista:</span>{' '}
                    {employees.find((employee) => employee.id === finalizeTarget.employee_id)?.name || finalizeTarget.employee_id}
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Car className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold">Veículo:</span>{' '}
                    {vehicles.find((vehicle) => vehicle.id === finalizeTarget.vehicle_id)?.brand || finalizeTarget.vehicle_id}
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Clock className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold">Início:</span>{' '}
                    {new Date(finalizeTarget.date).toLocaleString('pt-BR')}
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Timer className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold">Duração total:</span>{' '}
                    <span className="font-mono tabular-nums">{formatDuration(timers[finalizeTarget.id] || 0)}</span>
                  </div>
                </div>
              </div>
            </div>
            <FieldWrapper
              label={<span className="inline-flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5 stroke-[1.9] text-slate-500" /> Foto de check-out</span>}
              required
              error={formFinalizeErrors.checkoutImage}
              hint="Fotografe o estado final do veículo (hodômetro, combustível, lataria)"
            >
              <label className="group flex min-h-[110px] cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/40 px-4 py-6 text-center transition-all hover:border-emerald-400 hover:bg-emerald-50/40 focus-within:border-emerald-500 focus-within:ring-4 focus-within:ring-emerald-500/10">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-slate-400 ring-1 ring-slate-200 shadow-sm transition-all group-hover:text-emerald-600 group-hover:ring-emerald-200">
                  <Upload className="h-5 w-5 stroke-[1.9]" />
                </div>
                <div className="text-[13px] font-semibold text-slate-700 group-hover:text-emerald-700">Clique para anexar foto de check-out</div>
                <div className="text-[12px] font-medium text-slate-500">ou arraste e solte o arquivo</div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    try {
                      setFinalizeImage(await readFileAsDataUrl(file));
                      if (formFinalizeErrors.checkoutImage) setFormFinalizeErrors((p) => { const n = { ...p }; delete n.checkoutImage; return n; });
                    } catch (err) {
                      setFormFinalizeErrors({ checkoutImage: getApiErrorMessage(err, 'Erro ao carregar a foto.') });
                    }
                  }}
                  className="sr-only"
                />
              </label>
              {finalizeImage && (
                <div className="mt-3 inline-flex relative group/fim overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm w-fit">
                  <img src={finalizeImage} alt="Prévia check-out" className="h-24 w-24 object-cover transition-transform group-hover/fim:scale-[1.03]" />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/55 to-transparent opacity-0 transition-opacity group-hover/fim:opacity-100" />
                  <button
                    type="button"
                    onClick={() => setFinalizeImage(null)}
                    className="absolute top-1 right-1 inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-black/75 text-white opacity-0 shadow-lg transition-all hover:bg-rose-600 group-hover/fim:opacity-100 focus:outline-none focus:ring-2 focus:ring-rose-400/40 focus:ring-offset-1"
                    title="Remover foto"
                    aria-label="Remover foto check-out"
                  >
                    <X className="h-3.5 w-3.5 stroke-[2.2]" />
                  </button>
                </div>
              )}
            </FieldWrapper>
          </div>
        </Modal>
      )}
    </>
  );
};

export default CheckIn;
